// ==============================================
// FILE: frontend/src/components/AdminAuth.js
// ==============================================

import React, { useState, useEffect } from 'react';
import { Mail, Lock, User, Key, ShieldCheck, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { ROUTES, STORAGE_KEYS } from '../../config/api.config';

const AdminAuth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [approvalCode, setApprovalCode] = useState('');
  const [completeEmail, setCompleteEmail] = useState('');
  const [completePassword, setCompletePassword] = useState('');

  // Check if already logged in
  useEffect(() => {
    if (apiService.isAuthenticated()) {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (userData) {
        const user = JSON.parse(userData);
        if (user.role === 'super_admin') {
          window.location.href = ROUTES.SUPER_ADMIN_APPROVALS;
        } else {
          window.location.href = ROUTES.ADMIN_DASHBOARD;
        }
      }
    }
  }, []);

  const toggleForm = () => {
    setIsLogin(!isLogin);
    setError('');
    setSuccess('');
    setShowCompleteForm(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiService.login(loginEmail, loginPassword);

      if (response.success && response.data) {
        localStorage.setItem(STORAGE_KEYS.IS_ADMIN_LOGGED_IN, 'true');
        setSuccess('Login successful! Redirecting...');
        
        setTimeout(() => {
          if (response.data.user.role === 'super_admin') {
            window.location.href = ROUTES.SUPER_ADMIN_APPROVALS;
          } else {
            window.location.href = ROUTES.ADMIN_DASHBOARD;
          }
        }, 1200);
      } else {
        setError(response.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Check backend.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.requestAdminAccess(
        signupName,
        signupEmail,
        securityCode
      );

      if (response.success) {
        setSuccess('Request submitted! Check your email for approval code.');
        setShowCompleteForm(true);
        setCompleteEmail(signupEmail);
      } else {
        setError(response.error || 'Request failed');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Check backend.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiService.completeAdminRegistration(
        completeEmail,
        approvalCode,
        completePassword
      );

      if (response.success) {
        localStorage.setItem(STORAGE_KEYS.IS_ADMIN_LOGGED_IN, 'true');
        setSuccess('Registration complete! Redirecting...');
        
        setTimeout(() => {
          window.location.href = ROUTES.ADMIN_DASHBOARD;
        }, 1200);
      } else {
        setError(response.error || 'Registration failed');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Check backend.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 p-4 relative">
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-5%] w-96 h-96 bg-violet-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="relative" style={{ perspective: "1000px" }}>
          <div
            className={`transition-all duration-700 ${!isLogin ? "rotate-y-180" : ""}`}
            style={{ transformStyle: "preserve-3d" }}
          >

            {/* LOGIN FORM */}
            <div
              className="w-full"
              style={{
                backfaceVisibility: "hidden",
                transform: isLogin ? "rotateY(0deg)" : "rotateY(180deg)",
                position: isLogin ? "relative" : "absolute",
                top: 0,
                left: 0
              }}
            >
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-xl p-8 border border-white/50">

                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg mb-4">
                    <ShieldCheck size={40} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-700">Welcome Back</h2>
                  <p className="text-gray-500 text-sm mt-1">Sign in to access admin console</p>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex gap-3">
                    <AlertCircle className="text-red-600" size={20} />
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                {success && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex gap-3">
                    <CheckCircle className="text-green-600" size={20} />
                    <p className="text-green-700 text-sm">{success}</p>
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className="text-sm font-semibold text-gray-700 ml-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="admin@example.com"
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-cyan-500 focus:outline-none"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-700 ml-1">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-cyan-500 focus:outline-none"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:from-cyan-500 hover:to-blue-500 transition-all"
                  >
                    {loading ? (
                      <>
                        <Loader className="animate-spin" size={20} />
                        <span>Signing In...</span>
                      </>
                    ) : "Sign In"}
                  </button>
                </form>

                <p className="mt-6 text-center text-sm text-gray-600">
                  New Admin?{" "}
                  <button onClick={toggleForm} className="text-cyan-600 font-bold hover:underline">
                    Request Access
                  </button>
                </p>
              </div>
            </div>

            {/* SIGNUP & COMPLETE REGISTRATION FORM */}
            <div
              className="w-full"
              style={{
                backfaceVisibility: "hidden",
                transform: !isLogin ? "rotateY(0deg)" : "rotateY(-180deg)",
                position: !isLogin ? "relative" : "absolute",
                top: 0,
                left: 0
              }}
            >
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-xl p-8 border border-white/50">

                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 text-white shadow-lg mb-3">
                    <User size={28} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-700">
                    {showCompleteForm ? "Complete Registration" : "Admin Registration"}
                  </h2>
                  <p className="text-gray-500 text-sm">
                    {showCompleteForm ? "Enter approval code" : "Request admin access"}
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex gap-3">
                    <AlertCircle className="text-red-600" size={20} />
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                {success && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex gap-3">
                    <CheckCircle className="text-green-600" size={20} />
                    <p className="text-green-700 text-sm">{success}</p>
                  </div>
                )}

                {!showCompleteForm ? (
                  <form onSubmit={handleAdminRequest} className="space-y-4">
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        value={signupName}
                        onChange={(e) => setSignupName(e.target.value)}
                        placeholder="Full Name"
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none"
                        required
                      />
                    </div>

                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="email"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        placeholder="Official Email"
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none"
                        required
                      />
                    </div>

                    <div className="relative">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        value={securityCode}
                        onChange={(e) => setSecurityCode(e.target.value)}
                        placeholder="Security Clearance Code"
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:from-purple-500 hover:to-purple-600 transition-all"
                    >
                      {loading ? <Loader className="animate-spin" size={20} /> : "Request Access"}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleCompleteRegistration} className="space-y-4">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="email"
                        value={completeEmail}
                        onChange={(e) => setCompleteEmail(e.target.value)}
                        placeholder="Your Email"
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none"
                        required
                      />
                    </div>

                    <div className="relative">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        value={approvalCode}
                        onChange={(e) => setApprovalCode(e.target.value)}
                        placeholder="Approval Code"
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none"
                        required
                      />
                    </div>

                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="password"
                        value={completePassword}
                        onChange={(e) => setCompletePassword(e.target.value)}
                        placeholder="Create Password"
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:from-purple-500 hover:to-purple-600 transition-all"
                    >
                      {loading ? (
                        <>
                          <Loader className="animate-spin" size={20} />
                          Completing...
                        </>
                      ) : (
                        "Complete Registration"
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setShowCompleteForm(false);
                        setSuccess('');
                        setError('');
                      }}
                      className="w-full text-gray-600 mt-2 hover:text-gray-800"
                    >
                      ← Back to Request Form
                    </button>
                  </form>
                )}

                <p className="mt-6 text-center text-sm text-gray-600">
                  Already have access?{" "}
                  <button onClick={toggleForm} className="text-violet-600 font-bold hover:underline">
                    Login Here
                  </button>
                </p>

              </div>
            </div>

          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminAuth;